import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { getCompanyQueryData, setCompanyQueryData } from "#modules/company/infra/company.query";
import { deleteDefaultProcessStep } from "#modules/defaultProcessSteps/infra/deleteDefaultProcessStep.api";

export const useDeleteDefaultProcessStep = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  const { mutate, ...rest } = useMutation({
    mutationFn: deleteDefaultProcessStep,
    onMutate: ({ id }) => {
      const previousCompany = getCompanyQueryData(queryClient);

      setCompanyQueryData(queryClient, (currentCompany) => {
        const companyToUpdate = currentCompany ?? previousCompany;
        if (!companyToUpdate) {
          return companyToUpdate;
        }

        return {
          ...companyToUpdate,
          companySettings: {
            ...companyToUpdate.companySettings,
            defaultProcessSteps: companyToUpdate.companySettings.defaultProcessSteps.filter(
              (processStep) => processStep.id !== id,
            ),
          },
        };
      });

      return { previousCompany };
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (err: Error, _variables, context) => {
      showErrorToast({ title: err.message });
      errorHandler(err);

      if (context?.previousCompany) {
        setCompanyQueryData(queryClient, context.previousCompany);
      }
    },
  });

  return { deleteDefaultProcessStep: mutate, ...rest };
};
